<template>
  <div class="flex flex-col gap-[0.3rem]">
    <template v-if="readOnly">
      <!-- S6853: read-only caption is plain text next to a value div, not a form control label -->
      <div v-if="label" class="flex flex-wrap items-baseline gap-x-2 gap-y-1 min-h-[1.25rem]">
        <span class="block font-medium text-fg">{{ label }}</span>
      </div>
      <div :class="['p-2 text-fg', singleLine ? 'text-right w-full' : '']">
        <template v-if="hasValue">
          {{ displayValue }}
        </template>
        <template v-else>
          <span class="text-fg-muted italic">(blank)</span>
        </template>
      </div>
    </template>
    <template v-else>
      <BaseInput
        :id="id"
        :clearable="effectiveClearable"
        :error="combinedError"
        :label="label"
        :model-value="displayValue"
        :placeholder="placeholder"
        :readonly="false"
        :required="required"
        type="text"
        @change="onChange"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { formatDate, formatDateOnly, parseDate } from '@/helpers/DateFormatter.ts';
import BaseInput from '@/components/BaseComponents/BaseInput.vue';

const props = defineProps({
  /** Date | ISO string | null. Komponenten parser strenger ved behov. */
  modelValue: {
    type: [String, Date],
    default: null,
  },
  /** Tillat tom verdi (null) — slår på clearable og hindrer "påkrevd"-feil ved blanking. */
  nullable: {
    type: Boolean,
    default: false,
  },
  /** Vis og parse tidsdel (datetime2) i tillegg til datodel. */
  includeTime: {
    type: Boolean,
    default: false,
  },
  /**
   * Brukers foretrukne datoformat for parsing av input. 'DMY' (norsk default),
   * 'MDY' eller 'YMD'. Kallesteder som vil respektere user-prefs leverer dette
   * via `useUserPrefFormatting()`-composable; ellers benyttes default 'DMY'.
   */
  dateFormat: {
    type: String,
    required: false,
    default: 'DMY',
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  /** Høyrejuster i read-only — brukes for tabellceller. */
  singleLine: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  /** Eksternt feilmeldingstekst (form-validering). Kombineres med intern parse-feil. */
  error: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  /** Override clearable; default = nullable. */
  clearable: {
    type: Boolean,
    default: null,
  },
  id: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'parse-error']);

// Intern feil/utkast bindes til modelValue-verdien den oppstod mot: leverer
// parent en ny modelValue, er utkastet ikke lenger gyldig og faller bort.
const invalidDraft = ref(null);

const activeInvalidDraft = computed(() =>
  invalidDraft.value && invalidDraft.value.boundTo === props.modelValue
    ? invalidDraft.value
    : null,
);

const parseError = computed(() => activeInvalidDraft.value?.message ?? '');
const lastInvalidInput = computed(() => activeInvalidDraft.value?.input ?? null);

const effectiveClearable = computed(() =>
  props.clearable === null ? props.nullable : props.clearable,
);

const hasValue = computed(
  () => props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== '',
);

const parsedDate = computed(() => {
  if (!hasValue.value) return null;
  if (props.modelValue instanceof Date) return props.modelValue;
  return parseDate(String(props.modelValue), props.dateFormat);
});

const displayValue = computed(() => {
  // Beholder ugyldig input mens brukeren retter — slik at de ser hva de skrev.
  if (lastInvalidInput.value !== null) return lastInvalidInput.value;
  if (!parsedDate.value) return '';
  return props.includeTime
    ? formatDate(parsedDate.value)
    : formatDateOnly(parsedDate.value);
});

const combinedError = computed(() => {
  const ext = String(props.error ?? '').trim();
  if (ext) return ext;
  return parseError.value;
});

function parseTime (input) {
  let hour = 0;
  let minute = 0;
  let second = 0;
  let millisecond = 0;
  if (!input) return [hour, minute, second, millisecond];

  let secondPart;
  [hour, minute, secondPart] = input.split(':');
  if (secondPart) {
    [second, millisecond] = secondPart.split('.');
  }
  if (!millisecond) millisecond = 0;
  if (hour > 23 || hour < 0) hour = undefined;
  return [hour, minute, second, millisecond];
}

function parseInputString (input) {
  const trimmed = input.trim();

  // Aksepter ' ', ', ' og 'T' som skiller mellom dato- og tidsdel.
  let separator = ', ';
  if (trimmed.indexOf(', ') === -1) {
    separator = trimmed.indexOf('T') !== -1 ? 'T' : ' ';
  }

  const [datePart, timePart] = trimmed.split(separator);
  const dateValue = parseDate(datePart, props.dateFormat);
  if (!dateValue) return null;

  if (props.includeTime && timePart) {
    const [hour, minute, second, millisecond] = parseTime(timePart);
    if (hour === undefined) return null;
    // Kontrakt: bruker taster wall-clock-tid i sin lokale TZ. Komponenten emitter Date-er som
    // toISOString'er til UTC med 'Z'-suffix (yyyy-MM-ddTHH:mm:ss.fffZ) — lagring er alltid UTC.
    // parseDate ga oss en Date på UTC-midnatt; vi rekonstruerer en ny Date hvor y/m/d er fra
    // parsing-resultatet og h/m/s/ms er lokale komponenter slik at toISOString() korrekt
    // konverterer brukers tastede wall-clock-tid til UTC.
    return new Date(
      dateValue.getUTCFullYear(),
      dateValue.getUTCMonth(),
      dateValue.getUTCDate(),
      Number(hour),
      Number(minute),
      Number(second) || 0,
      Number(millisecond) || 0,
    );
  }

  return dateValue;
}

function onChange (value) {
  if (value === '' || value === null || value === undefined) {
    if (props.nullable) {
      invalidDraft.value = null;
      emit('update:modelValue', null);
      return;
    }
    // Behold eventuelt eksisterende ugyldig utkast i feltet, kun meldingen endres.
    invalidDraft.value = {
      boundTo: props.modelValue,
      input: activeInvalidDraft.value?.input ?? null,
      message: 'Feltet kan ikke være tomt',
    };
    emit('parse-error', parseError.value);
    return;
  }

  const dateValue = parseInputString(value);
  if (!dateValue || Number.isNaN(dateValue.getTime())) {
    invalidDraft.value = {
      boundTo: props.modelValue,
      input: value,
      message: 'Ugyldig datoformat.',
    };
    emit('parse-error', parseError.value);
    return;
  }

  invalidDraft.value = null;
  emit('update:modelValue', dateValue);
}
</script>

